import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, Typography, TextField, Button, Alert, Card, CardContent, CircularProgress } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/store';
import { resetPassword } from '@/store/slices/authSlice';

const schema = yup.object<{ password: string; confirmPassword: string }>({
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/, 'Password must contain uppercase, lowercase, number, and special character')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Password confirmation does not match')
    .required('Please confirm your password'),
}).required();

const ResetPassword: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [successMsg, setSuccessMsg] = React.useState<string | null>(null);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const { control, handleSubmit } = useForm<{ password: string; confirmPassword: string }>({
    resolver: yupResolver(schema) as any,
    defaultValues: { password: '', confirmPassword: '' },
  });

  const onSubmit = async (data: { password: string }) => {
    if (!token) {
      setErrorMsg('Missing reset token');
      return;
    }
    setErrorMsg(null);
    setSuccessMsg(null);
    setLoading(true);
    try {
      const res = await dispatch(resetPassword({ token, password: data.password })).unwrap();
      setSuccessMsg(res?.message || 'Password has been reset');
      setTimeout(() => navigate('/login'), 1400);
    } catch (err: any) {
      setErrorMsg(err || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Card sx={{ width: '100%', maxWidth: 480 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
              Choose a new password
            </Typography>

            {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
            {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="password"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="New Password"
                    type="password"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="confirmPassword"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Confirm Password"
                    type="password"
                    fullWidth
                    margin="normal"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={loading} startIcon={loading ? <CircularProgress size={18} /> : null}>
                {loading ? 'Saving...' : 'Reset password'}
              </Button>

              <Button fullWidth variant="text" sx={{ mt: 1 }} onClick={() => navigate('/login')}>
                Back to login
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ResetPassword;
